<template>
  <main class="container is-fluid">
    <div class="columns">
      <div class="field column is-one-third">
        <div class="has-text-right mb-2">
          <CreateForm v-on:worker-created="updateWorkersPage" />
        </div>

        <div
          v-if="!processId && !local"
          class="tabs is-centered mb-2"
        >
          <ul>
            <li
              :class="{ 'is-active': !archivedWorkers }"
              v-on:click="archivedWorkers = false"
            >
              <a>Available workers</a>
            </li>
            <li
              :class="{ 'is-active': archivedWorkers }"
              v-on:click="archivedWorkers = true"
            >
              <a>Archived workers</a>
            </li>
          </ul>
        </div>

        <div class="field has-addons">
          <!-- Selection of a worker is required to list versions -->
          <div
            class="control"
            v-if="!workerType"
          >
            <div class="select">
              <select v-model="typeFilter">
                <option value="">Filter by worker type…</option>
                <option
                  v-for="t in workerTypes"
                  :key="t.id"
                  :value="t.slug"
                >
                  {{ truncateShort(t.display_name) }}
                </option>
              </select>
            </div>
          </div>
          <div class="control is-expanded">
            <form
              v-if="workersPage"
              class="field"
              v-on:submit.prevent="filter"
            >
              <input
                class="input"
                type="text"
                v-model="nameFilter"
                placeholder="Filter by name…"
              />
            </form>
          </div>
        </div>
        <div class="control">
          <Paginator
            :response="workersPage"
            v-slot="{ results }"
            :loading="loading"
            v-model:page="page"
            singular="worker"
            plural="workers"
          >
            <table class="table is-fullwidth is-hoverable">
              <thead>
                <tr>
                  <th>Name</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="worker in results"
                  :key="worker.id"
                  v-on:click="selectedWorkerId = worker.id"
                  class="is-clickable"
                  :class="{ 'is-selected': selectedWorkerId === worker.id }"
                >
                  <td>
                    <WorkerTag :worker="worker" />
                  </td>
                </tr>
              </tbody>
            </table>
          </Paginator>
        </div>
      </div>
      <div class="field column is-two-thirds">
        <WorkerDetails
          v-if="selectedWorkerId"
          :worker-id="selectedWorkerId"
          :selectable="selectable"
          :process-id="processId"
          :local="local"
          :simple="selectable || processId !== null || local"
          v-on:selected-version="(version) => $emit('selected-version', version)"
        />
        <div
          v-else
          class="notification is-info"
        >
          Please select a <strong>worker</strong> on the left panel.
        </div>
      </div>
    </div>
  </main>
</template>

<script lang="ts">
import { isEmpty } from 'lodash'
import { mapActions, mapState } from 'pinia'
import { type PropType, defineComponent } from 'vue'

import type { ListWorkersParameters } from '@/api'
import Paginator from '@/components/Paginator.vue'
import CreateForm from '@/components/Process/Workers/CreateForm.vue'
import WorkerDetails from '@/components/Process/Workers/Details.vue'
import WorkerTag from '@/components/Process/Workers/WorkerRuns/WorkerTag.vue'
import { errorParser } from '@/helpers'
import { truncateMixin } from '@/mixins'
import { useAuthStore, useNotificationStore, useWorkerStore } from '@/stores'
import type { PageNumberPagination, UUID } from '@/types'
import type { Worker, WorkerVersion } from '@/types/worker'

export default defineComponent({
  components: {
    Paginator,
    WorkerTag,
    CreateForm,
    WorkerDetails,
  },
  mixins: [truncateMixin],
  emits: {
    'selected-version': (value: WorkerVersion) =>
      typeof value === 'object' && value !== null && typeof value.id === 'string',
  },
  props: {
    /**
     * Whether or not buttons are shown to allow selecting a worker version.
     * This will trigger `selected-version` events.
     *
     * Without `processId` or `local` set, any worker version can be selected.
     */
    selectable: {
      type: Boolean,
      default: false,
    },
    /**
     * If `selectable` is set, selecting a version will both cause an event
     * and create a WorkerRun on the specified process.
     *
     * Only available versions will be selectable.
     */
    processId: {
      type: String as PropType<UUID | null>,
      default: null,
    },
    /**
     * If true, only worker versions that do not have a revision attached
     * can be selected (for creating local worker runs).
     *
     * This has no effect if `selectable` is not set.
     */
    local: {
      type: Boolean,
      default: false,
    },
    /**
     * Optionally, a worker type filter can be passed by the parent component.
     */
    workerType: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    loading: false,
    workersPage: undefined as PageNumberPagination<Worker> | undefined,
    selectedWorkerId: null as UUID | null,
    page: 1,
    nameFilter: '',
    typeFilter: '',
    archivedWorkers: false,
  }),
  computed: {
    ...mapState(useWorkerStore, ['workerTypes']),
    ...mapState(useAuthStore, ['hasFeature', 'user']),
  },
  methods: {
    ...mapActions(useNotificationStore, ['notify']),
    ...mapActions(useWorkerStore, ['listWorkers', 'listWorkerTypes']),
    async updateWorkersPage() {
      this.loading = true
      if (isEmpty(this.workerTypes)) await this.fetchWorkerTypes()
      try {
        const payload: ListWorkersParameters = { page: this.page, archived: this.archivedWorkers }
        if (this.nameFilter) payload.name = this.nameFilter
        if (this.workerType) payload.type = this.workerTypeIdFromSlug(this.workerType)
        else if (this.typeFilter) payload.type = this.typeFilter
        this.workersPage = await this.listWorkers(payload)
      } catch (err) {
        this.notify({
          type: 'error',
          text: `An error occurred listing workers: ${errorParser(err)}`,
        })
      } finally {
        this.loading = false
      }
    },
    filter() {
      // Update workers list. Reset page if required
      if (this.page === 1) return this.updateWorkersPage()
      this.page = 1
    },
    async fetchWorkerTypes() {
      this.loading = true
      try {
        await this.listWorkerTypes()
      } catch (err) {
        this.notify({
          type: 'error',
          text: `An error occurred listing worker types: ${errorParser(err)}`,
        })
      } finally {
        this.loading = false
      }
    },
    workerTypeIdFromSlug(slug: string): UUID | undefined {
      return Object.keys(this.workerTypes).find((type) => this.workerTypes[type].slug === slug)
    },
  },
  watch: {
    page: {
      immediate: true,
      handler: 'updateWorkersPage',
    },
    typeFilter: 'filter',
    archivedWorkers() {
      this.page = 1
      this.updateWorkersPage()
    },
  },
})
</script>

<style lang="scss" scoped>
.container {
  height: 100%;
  > .columns {
    height: inherit;
    > .column {
      overflow: auto;
    }
  }
}
.tabs {
  width: 100%;
}
.tabs a {
  padding-top: 0;
}
</style>
